import request from "superagent";
import { url as urlUtil } from "@genx/july";
import _each from "lodash/each";

const AllowedMethods = {
    get: "get",
    post: "post",
    put: "put",
    del: "del",
    delete: "del",
    upload: "post",
    download: "get",
};

// The WAF challenges a request by redirecting to a page that solves it in the
// background. Wait long enough for a slow solve, then give up and surface the
// original error rather than hanging the caller.
const WAF_CHALLENGE_ATTEMPTS = 30;
const WAF_CHALLENGE_INTERVAL = 1000;
const WAF_CHALLENGE_STATUS = 307;

function resToPath(parts) {
    return parts
        ? Array.isArray(parts)
            ? parts.map((res) => encodeURIComponent(res)).join("/")
            : parts
        : "";
}

function setUploadBody(req, body, options) {
    if (options && options.formData) {
        options.formData.entries.forEach(([k, v]) => {
            req.field(k, v);
        });
    }

    req.attach(options && options.fileField ? options.fileField : "file", body);
}

/**
 * Read the AWS WAF token when one is already available.
 *
 * `AwsWafIntegration` comes from the challenge.js script loaded in index.html,
 * so it is missing wherever WAF is not enabled. `hasToken()` is a synchronous
 * check, which keeps this off the critical path: requests go out without a
 * token and are replayed if AWS WAF answers with its challenge.
 *
 * @returns {Promise<string|null>}
 */
export const peekWafToken = async () => {
    const integration = window.AwsWafIntegration;

    if (!integration || typeof integration.hasToken !== "function") {
        return null;
    }

    try {
        if (!integration.hasToken()) {
            return null;
        }

        return (await integration.getToken()) || null;
    } catch (error) {
        return null;
    }
};

/**
 * Trigger the AWS WAF challenge and wait for a token to come back.
 *
 * Only used to replay a request that AWS WAF answered with a challenge, where
 * waiting is what the user is already doing. Polls because the challenge runs
 * in the background and has no completion callback.
 *
 * Exported so call sites that bypass this client can attach the same header.
 *
 * @returns {Promise<string|null>}
 */
export const getWafToken = async () => {
    const integration = window.AwsWafIntegration;

    if (!integration) {
        return null;
    }

    try {
        if (typeof integration.hasToken === "function" && integration.hasToken()) {
            const token = await integration.getToken();
            if (token) return token;
        }

        if (typeof integration.forceRefreshToken !== "function") {
            return null;
        }

        await integration.forceRefreshToken();

        for (let attempt = 0; attempt < WAF_CHALLENGE_ATTEMPTS; attempt++) {
            if (integration.hasToken()) {
                const token = await integration.getToken();
                if (token) return token;
            }

            await new Promise((resolve) => setTimeout(resolve, WAF_CHALLENGE_INTERVAL));
        }
    } catch (error) {
        // A missing token must not break the request itself.
    }

    return null;
};

/**
 * Http client.
 * @class
 */
export default class HttpClient {
    /**
     * HttpClient constructor
     * @param {*} endpoint - Default endpoint
     */
    constructor(endpoint) {
        endpoint != null || (endpoint = "");
        this.endpoint = endpoint;
    }

    /**
     * Http get request.
     * @param {string|Array} resource
     * @param {Object} query
     * @param {Object} options
     * @property {string} [options.endpoint] - Override the default base endpoint
     * @property {Function} [options.onProgress] - Report progress when waiting for response
     */
    async get(resource, query, options) {
        return this._send("get", resToPath(resource), query, null, options);
    }

    /**
     * Http post request.
     * @param {string|Array} resource
     * @param {Object} data
     * @param {Object} query
     * @param {Object} options
     * @property {string} [options.endpoint] - Override the default base endpoint
     * @property {Function} [options.onProgress] - Report progress when waiting for response
     */
    async post(resource, data, query, options) {
        return this._send("post", resToPath(resource), query, data, options);
    }

    /**
     * Http put request.
     * @param {string|Array} resource
     * @param {Object} data
     * @param {Object} query
     * @param {Object} options
     * @property {string} [options.endpoint] - Override the default base endpoint
     * @property {Function} [options.onProgress] - Report progress when waiting for response
     */
    async put(resource, data, query, options) {
        return this._send("put", resToPath(resource), query, data, options);
    }

    /**
     * Http del request.
     * @param {string|Array} resource
     * @param {Object} query
     * @param {Object} options
     * @property {string} [options.endpoint] - Override the default base endpoint
     * @property {Function} [options.onProgress] - Report progress when waiting for response
     */
    async del(resource, query, options) {
        return this._send("del", resToPath(resource), query, null, options);
    }

    /**
     * Http upload request.
     * @param {string|Array} resource
     * @param {string} file
     * @param {Object} query
     * @param {Object} options
     * @property {string} [options.endpoint] - Override the default base endpoint
     * @property {Function} [options.onProgress] - Report progress when waiting for response
     * @property {Object} [options.formData] - Form data passed while uploading
     * @property {string} [options.fileField="file"] - File field name, default as "file"
     */
    async upload(resource, file, query, options) {
        return this._send(
            "upload",
            resToPath(resource),
            query,
            file,
            options,
            null
        );
    }

    /**
     * Http download request.
     * @param {string|Array} resource
     * @param {Object} query
     * @param {Object} options
     * @property {string} [options.endpoint] - Override the default base endpoint
     * @property {Function} [options.onProgress] - Report progress when waiting for response
     */
    async download(resource, query, options) {
        return this._send(
            "download",
            resToPath(resource),
            query,
            null,
            options
        );
    }

    async _send(method, path, query, body, options, setRequestBody) {
        method = method.toLowerCase();
        const httpMethod = options?.httpMethod || AllowedMethods[method];
        if (!httpMethod) {
            throw new Error("Invalid method: " + method);
        }

        const url =
            path.startsWith("http:") ||
            path.startsWith("https:") ||
            !this.endpoint
                ? path // absolute url
                : urlUtil.join(
                      options && options.endpoint
                          ? options.endpoint
                          : this.endpoint,
                      path
                  );

        const req = this._createRequest(httpMethod, url);

        if (this.onSending) {
            this.onSending(req, httpMethod, url);
        }

        // The aws-waf-token cookie is domain scoped, so it is not attached to
        // calls made across host domains. AWS WAF reads the token from this
        // header in addition to the cookie. challenge.js already fetches one in
        // the background on page load, so attach it whenever it is ready and
        // otherwise let the request go and replay it if the WAF challenges.
        if (this._isApiRequest(url)) {
            const wafToken = await peekWafToken();
            if (wafToken) {
                req.set("x-aws-waf-token", wafToken);
            }
        }

        const extraHeaders = options?.headers;
        if (extraHeaders) {
            _each(extraHeaders, (v, k) => {
                req.set(k, v);
            });
        }

        // Defaults to the runtime flag set in public/index.html so the whole app
        // can be switched at once. Callers can still opt out per request.
        if (options?.withCredentials ?? window.withCredentials) {
            req.withCredentials();
        }

        if (options?.responseType) {
            req.responseType(options.responseType);
        }

        // if(api.enableCA){
        //     // req.ca(cert);
        // }

        if (query) {
            req.query(query);
        }

        if (setRequestBody) {
            setRequestBody(req, body, options);
        } else {
            if (method === "upload") {
                setUploadBody(req, body, options);
            } else {
                req.send(body);
            }
        }

        if (options && options.onProgress) {
            req.on("progress", options.onProgress);
        }

        try {
            const res = await req;
            const result = res.body || res.text;

            if (this.onSent) {
                await this.onSent(url, result);
            }

            return result;
        } catch (error) {
            // AWS WAF answers a request that has no valid token by redirecting
            // to its challenge page. Solve it, then replay the request once.
            if (
                error.status === WAF_CHALLENGE_STATUS &&
                this._isApiRequest(url) &&
                !options?.wafRetried
            ) {
                const wafToken = await getWafToken();

                if (wafToken) {
                    return this._send(
                        method,
                        path,
                        query,
                        body,
                        {
                            ...options,
                            wafRetried: true,
                            headers: {
                                ...options?.headers,
                                "x-aws-waf-token": wafToken,
                            },
                        },
                        setRequestBody
                    );
                }
            }

            if (error.response && error.response.body) {
                if (error.response.body instanceof Blob) {
                    const parsedError = JSON.parse(
                        await error.response.body.text(),
                    )?.error;
                    if (this.onReponseError) {
                        return this.onReponseError(parsedError);
                    }
                    throw parsedError;
                }
                if (this.onReponseError) {
                    return this.onReponseError(error.response.body);
                }

                throw error;
            }

            if (this.onOtherError) {
                return this.onOtherError(error);
            }

            throw error;
        }
    }

    _createRequest(httpMethod, url) {
        return request[httpMethod](url);
    }

    /**
     * Whether a request goes to this client's own API.
     *
     * Only the CRM API sits behind AWS WAF. Attaching the token header to any
     * other host would add a non-simple header and force a preflight that host
     * is unlikely to allow.
     *
     * @param {string} url - Resolved request url
     * @returns {boolean}
     */
    _isApiRequest(url) {
        return this.endpoint ? url.startsWith(this.endpoint) : false;
    }
}
